// SettingsModal.js
import React, { useState, useEffect } from 'react';
import { X, Bell, Settings, AlertCircle } from 'lucide-react';
import {
    requestNotificationPermission,
    getFCMToken,
    setBirthdayReminder,
    setTodoReminder,
    setFollowUpReminder,
    getDeviceLinkInfo,
    registerSecondaryDevice,
    unregisterSecondaryDevice
} from '../firebase';
import { driveUploadSnapshot, driveDownloadSnapshot } from '../utils/driveSync';
import { idbSetSetting, idbGetSetting } from '../utils/idb';

// 5분 단위로 반올림하는 함수
const roundToNearest5Minutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const roundedMinutes = Math.round(minutes / 5) * 5;

    if (roundedMinutes >= 60) {
        return `${String(hours + 1).padStart(2, '0')}:00`;
    } else {
        return `${String(hours).padStart(2, '0')}:${String(roundedMinutes).padStart(2, '0')}`;
    }
};

const SettingsModal = ({ isOpen, onClose, appStatus }) => {
    // 푸시 알림 관련 상태 (직접 관리)
    const [isSupported, setIsSupported] = useState(false);
    const [permission, setPermission] = useState('default');
    const [isLoading, setIsLoading] = useState(false);

    const [isEnabled, setIsEnabled] = useState(false);
    const [birthdayEnabled, setBirthdayEnabled] = useState(true);
    const [birthdayTime, setBirthdayTime] = useState('09:00');
    const [birthdayTimeLoading, setBirthdayTimeLoading] = useState(false);
    const [todoEnabled, setTodoEnabled] = useState(true);
    const [todoTime, setTodoTime] = useState('09:05');
    const [todoTimeLoading, setTodoTimeLoading] = useState(false);
    const [followUpReminderEnabled, setFollowUpReminderEnabled] = useState(false);
    const [followUpReminderTime, setFollowUpReminderTime] = useState('17:00');
    const [followUpReminderTimeLoading, setFollowUpReminderTimeLoading] = useState(false);

    // 동기화 진행 상태
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncPhase, setSyncPhase] = useState(null); // 'upload' | 'download' | null

    // 기기 관리 상태
    const [linkInfo, setLinkInfo] = useState(null);
    const [secondaryInput, setSecondaryInput] = useState('');
    const [linkLoading, setLinkLoading] = useState(false);

    // 푸시 알림 초기화
    useEffect(() => {
        const initializePushNotifications = async () => {
            try {
                // 브라우저 지원 확인
                if (!('Notification' in window) || !('serviceWorker' in navigator)) {
                    setIsSupported(false);
                    return;
                }
                setIsSupported(true);

                // 알림 권한 확인
                const currentPermission = Notification.permission;
                setPermission(currentPermission);

                if (currentPermission === 'granted') {
                    // FCM 토큰 가져오기
                    await getFCMToken();
                }
            } catch (error) {
                console.error('푸시 알림 초기화 실패:', error);
            }
        };

        if (isOpen) {
            initializePushNotifications();
        }
    }, [isOpen]);

    // 저장된 토글 상태 불러오기
    useEffect(() => {
        const loadSettings = async () => {
            try {
                // 푸시 알림 활성화 상태 (localStorage 유지 - 다른 용도로 사용될 수 있음)
                const savedState = localStorage.getItem('pushNotificationEnabled');
                if (savedState !== null) {
                    setIsEnabled(JSON.parse(savedState));
                }

                // 생일 알림 설정 (IndexedDB 사용)
                const birthdayEnabled = await idbGetSetting('birthdayReminderEnabled');
                if (birthdayEnabled !== undefined && birthdayEnabled !== null) {
                    setBirthdayEnabled(birthdayEnabled === 'true' || birthdayEnabled === true);
                } else {
                    // 기본값: 활성화
                    setBirthdayEnabled(true);
                    await idbSetSetting('birthdayReminderEnabled', 'true');
                }

                const birthdayTime = await idbGetSetting('birthdayReminderTime');
                if (birthdayTime) {
                    setBirthdayTime(birthdayTime);
                } else {
                    // 기본값: 09:00
                    setBirthdayTime('09:00');
                    await idbSetSetting('birthdayReminderTime', '09:00');
                }

                // 할일 알림 설정 (IndexedDB 사용)
                const todoEnabled = await idbGetSetting('todoReminderEnabled');
                if (todoEnabled !== undefined && todoEnabled !== null) {
                    setTodoEnabled(todoEnabled === 'true' || todoEnabled === true);
                } else {
                    // 기본값: 활성화
                    setTodoEnabled(true);
                    await idbSetSetting('todoReminderEnabled', 'true');
                }

                const todoTime = await idbGetSetting('todoReminderTime');
                if (todoTime) {
                    setTodoTime(todoTime);
                } else {
                    // 기본값: 09:05
                    setTodoTime('09:05');
                    await idbSetSetting('todoReminderTime', '09:05');
                }

                // Follow Up 알림 설정 (IndexedDB 사용)
                const followUpEnabled = await idbGetSetting('followUpReminderEnabled');
                if (followUpEnabled !== undefined && followUpEnabled !== null) {
                    setFollowUpReminderEnabled(followUpEnabled === 'true' || followUpEnabled === true);
                } else {
                    // 기본값: 비활성화
                    setFollowUpReminderEnabled(false);
                    await idbSetSetting('followUpReminderEnabled', 'false');
                }

                const followUpReminderTime = await idbGetSetting('followUpReminderTime');
                if (followUpReminderTime) {
                    setFollowUpReminderTime(followUpReminderTime);
                } else {
                    // 기본값: 17:00
                    setFollowUpReminderTime('17:00');
                    await idbSetSetting('followUpReminderTime', '17:00');
                }
            } catch (error) {
                console.error('설정 로드 실패:', error);
            }
        };
        loadSettings();
    }, []);

    // ACTIVE일 때 기기 링크 상태 로드
    useEffect(() => {
        const loadLink = async () => {
            try {
                const primaryId = localStorage.getItem('deviceId');
                if (!primaryId) return;
                const info = await getDeviceLinkInfo(primaryId);
                setLinkInfo(info);
            } catch (e) { console.warn('기기 링크 정보 로드 실패', e); }
        };
        if (isOpen && appStatus === 'active') {
            loadLink();
        }
    }, [isOpen, appStatus]);

    // 권한 상태 확인
    useEffect(() => {
        if (isOpen) {
            // 저장된 상태와 실제 권한 상태를 비교
            const savedState = localStorage.getItem('pushNotificationEnabled');
            if (savedState !== null) {
                const savedEnabled = JSON.parse(savedState);
                setIsEnabled(savedEnabled && permission === 'granted');
            } else {
                setIsEnabled(permission === 'granted');
            }
        }
    }, [isOpen, permission]);

    // 테스트 알림 기능 제거

    // 푸시 알림 토글
    const handleTogglePushNotification = async () => {
        if (isEnabled) {
            // 비활성화
            setIsEnabled(false);
            localStorage.setItem('pushNotificationEnabled', 'false');
        } else {
            // 활성화
            try {
                setIsLoading(true);
                const granted = await requestNotificationPermission();
                if (granted) {
                    setPermission('granted');
                    const fcmToken = await getFCMToken();
                    if (fcmToken) {
                        setIsEnabled(true);
                        localStorage.setItem('pushNotificationEnabled', 'true');
                    } else {
                        console.warn('FCM 토큰을 가져오지 못했습니다.');
                    }
                } else {
                    setPermission('denied');
                    console.warn('알림 권한 거부됨');
                }
            } catch (error) {
                console.error('푸시 알림 활성화 실패:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    // 상태 패널 제거

    // 모달 열림/닫힘 시 body 스크롤 제어
    useEffect(() => {
        if (isOpen) {
            // 모달이 열릴 때: body 스크롤 방지
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollbarWidth}px`; // 스크롤바 너비만큼 패딩 추가 (레이아웃 시프트 방지)
        } else {
            // 모달이 닫힐 때: body 스크롤 복원
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        }

        // cleanup: 컴포넌트 언마운트 시에도 스크롤 복원
        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* 배경 오버레이 */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            ></div>

            {/* 모달 컨텐츠 */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto" data-settings-modal-content>
                {/* 헤더 */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                            <Settings className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-800">설정</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* 컨텐츠 */}
                <div className="p-6 space-y-6">

                    {/* 알림 설정 (섹션 카드) */}
                    <div>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-4">
                                <p className="font-medium text-gray-800">알림 설정</p>
                                {permission === 'granted' ? (
                                    <span className="text-xs px-2 py-1 rounded bg-green-50 text-green-700">권한 허용됨</span>
                                ) : (
                                    <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">권한 미허용</span>
                                )}
                            </div>

                            <div className="space-y-4">
                                {/* 전체 푸시 토글 */}
                                <div className="flex items-center justify-between p-4 bg-white rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <Bell className="w-5 h-5 text-gray-600" />
                                        <div>
                                            <p className="font-medium text-gray-800">푸시 알림</p>
                                            <p className="text-sm text-gray-600">앱의 전체 알림 수신을 제어합니다</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleTogglePushNotification}
                                        disabled={isLoading || !isSupported || permission === 'denied'}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isEnabled ? 'bg-blue-600' : 'bg-gray-300'} ${isLoading || !isSupported || permission === 'denied' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>

                                {/* 고객생일 알림 */}
                                <div className={`p-4 rounded-xl ${isEnabled ? 'bg-white' : 'bg-gray-100 opacity-60'}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Bell className="w-5 h-5 text-pink-600" />
                                            <div>
                                                <p className="font-medium text-gray-800">고객생일 알림받기</p>
                                                <p className="text-sm text-gray-600">Birthday Reminder</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={async () => {
                                                if (!isEnabled) return;
                                                const next = !birthdayEnabled;
                                                setBirthdayEnabled(next);
                                                await idbSetSetting('birthdayReminderEnabled', String(next));
                                                try {
                                                    const deviceId = localStorage.getItem('deviceId');
                                                    console.log('[SettingsModal] setBirthdayReminder toggle ->', { deviceId, enabled: next, time: birthdayTime });
                                                    const res = await setBirthdayReminder({ deviceId, enabled: next, time: birthdayTime });
                                                    console.log('[SettingsModal] setBirthdayReminder result:', res?.data);
                                                    if (res?.data?.success) {
                                                        alert('생일 알림 설정이 저장되었습니다.');
                                                    }
                                                } catch (e) { console.warn('[SettingsModal] setBirthdayReminder failed', e); }
                                            }}
                                            disabled={!isEnabled}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${birthdayEnabled ? 'bg-pink-600' : 'bg-gray-300'} ${!isEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${birthdayEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                    </div>
                                    <div className="mt-3 flex items-center justify-center gap-2">
                                        <input
                                            type="time"
                                            step="300"
                                            value={birthdayTime}
                                            onChange={async (e) => {
                                                const roundedTime = roundToNearest5Minutes(e.target.value);
                                                setBirthdayTime(roundedTime);
                                                await idbSetSetting('birthdayReminderTime', roundedTime);
                                            }}
                                            onBlur={async (e) => {
                                                if (isEnabled && birthdayEnabled) {
                                                    try {
                                                        setBirthdayTimeLoading(true);
                                                        const deviceId = localStorage.getItem('deviceId');
                                                        console.log('[SettingsModal] setBirthdayReminder time ->', { deviceId, enabled: true, time: e.target.value });
                                                        const res = await setBirthdayReminder({ deviceId, enabled: true, time: e.target.value });
                                                        console.log('[SettingsModal] setBirthdayReminder result:', res?.data);
                                                        if (res?.data?.success) {
                                                            alert('생일 알림 시간이 저장되었습니다.');
                                                        }
                                                    } catch (e2) { 
                                                        console.warn('[SettingsModal] setBirthdayReminder failed', e2);
                                                        alert('생일 알림 시간 저장 중 오류가 발생했습니다.');
                                                    } finally {
                                                        setBirthdayTimeLoading(false);
                                                    }
                                                }
                                            }}
                                            disabled={!isEnabled || !birthdayEnabled || birthdayTimeLoading}
                                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center disabled:opacity-50"
                                        />
                                        {birthdayTimeLoading && (
                                            <span className="inline-block w-4 h-4 rounded-full border-2 border-pink-300 border-t-pink-600 animate-spin"></span>
                                        )}
                                    </div>
                                </div>

                                {/* 오늘의 할일 알림 */}
                                <div className={`p-4 rounded-xl ${isEnabled ? 'bg-white' : 'bg-gray-100 opacity-60'}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Bell className="w-5 h-5 text-orange-600" />
                                            <div>
                                                <p className="font-medium text-gray-800">오늘의 할일 알림받기</p>
                                                <p className="text-sm text-gray-600">업무시작시간에 맞추어 놓고 알림을 받아보세요</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={async () => {
                                                if (!isEnabled) return;
                                                const next = !todoEnabled;
                                                setTodoEnabled(next);
                                                await idbSetSetting('todoReminderEnabled', String(next));
                                                try {
                                                    const deviceId = localStorage.getItem('deviceId');
                                                    console.log('[SettingsModal] setTodoReminder toggle ->', { deviceId, enabled: next, time: todoTime });
                                                    const res = await setTodoReminder({ deviceId, enabled: next, time: todoTime });
                                                    console.log('[SettingsModal] setTodoReminder result:', res?.data);
                                                    if (res?.data?.success) {
                                                        alert('할일 알림 설정이 저장되었습니다.');
                                                    }
                                                } catch (e) { console.warn('[SettingsModal] setTodoReminder failed', e); }
                                            }}
                                            disabled={!isEnabled}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ml-2 flex-shrink-0 ${todoEnabled ? 'bg-orange-600' : 'bg-gray-300'} ${!isEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${todoEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                    </div>
                                    <div className="mt-3 flex items-center justify-center gap-2">
                                        <input
                                            type="time"
                                            step="300"
                                            value={todoTime}
                                            onChange={async (e) => {
                                                const roundedTime = roundToNearest5Minutes(e.target.value);
                                                setTodoTime(roundedTime);
                                                await idbSetSetting('todoReminderTime', roundedTime);
                                            }}
                                            onBlur={async (e) => {
                                                if (isEnabled && todoEnabled) {
                                                    try {
                                                        setTodoTimeLoading(true);
                                                        const deviceId = localStorage.getItem('deviceId');
                                                        console.log('[SettingsModal] setTodoReminder time ->', { deviceId, enabled: true, time: e.target.value });
                                                        const res = await setTodoReminder({ deviceId, enabled: true, time: e.target.value });
                                                        console.log('[SettingsModal] setTodoReminder result:', res?.data);
                                                        if (res?.data?.success) {
                                                            alert('할일 알림 시간이 저장되었습니다.');
                                                        }
                                                    } catch (e2) { 
                                                        console.warn('[SettingsModal] setTodoReminder failed', e2);
                                                        alert('할일 알림 시간 저장 중 오류가 발생했습니다.');
                                                    } finally {
                                                        setTodoTimeLoading(false);
                                                    }
                                                }
                                            }}
                                            disabled={!isEnabled || !todoEnabled || todoTimeLoading}
                                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center disabled:opacity-50"
                                        />
                                        {todoTimeLoading && (
                                            <span className="inline-block w-4 h-4 rounded-full border-2 border-orange-300 border-t-orange-600 animate-spin"></span>
                                        )}
                                    </div>
                                </div>

                                {/* 오늘할일 리마인더 */}
                                <div className={`p-4 rounded-xl ${isEnabled ? 'bg-white' : 'bg-gray-100 opacity-60'}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Bell className="w-5 h-5 text-blue-600" />
                                            <div>
                                                <p className="font-medium text-gray-800">오늘할일 리마인더</p>
                                                <p className="text-sm text-gray-600">설정한 시간에 랜덤 Follow Up 알림</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={async () => {
                                                if (!isEnabled) return;
                                                const next = !followUpReminderEnabled;
                                                setFollowUpReminderEnabled(next);
                                                try {
                                                    await idbSetSetting('followUpReminderEnabled', String(next));
                                                    const deviceId = localStorage.getItem('deviceId');
                                                    console.log('[SettingsModal] setFollowUpReminder toggle ->', { deviceId, enabled: next, time: followUpReminderTime });
                                                    const res = await setFollowUpReminder({ deviceId, enabled: next, time: followUpReminderTime });
                                                    console.log('[SettingsModal] setFollowUpReminder result:', res?.data);
                                                    if (res?.data?.success) {
                                                        console.log('[SettingsModal] Follow Up 알림 설정 저장 완료:', next);
                                                    }
                                                } catch (error) {
                                                    console.error('Follow Up 알림 설정 저장 실패:', error);
                                                }
                                            }}
                                            disabled={!isEnabled}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ml-2 flex-shrink-0 ${followUpReminderEnabled ? 'bg-blue-600' : 'bg-gray-300'} ${!isEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${followUpReminderEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                    </div>
                                    <div className="mt-3 flex items-center justify-center gap-2">
                                        <input
                                            type="time"
                                            step="300"
                                            value={followUpReminderTime}
                                            onChange={async (e) => {
                                                const roundedTime = roundToNearest5Minutes(e.target.value);
                                                setFollowUpReminderTime(roundedTime);
                                                await idbSetSetting('followUpReminderTime', roundedTime);
                                            }}
                                            onBlur={async (e) => {
                                                if (isEnabled && followUpReminderEnabled) {
                                                    try {
                                                        setFollowUpReminderTimeLoading(true);
                                                        const deviceId = localStorage.getItem('deviceId');
                                                        console.log('[SettingsModal] setFollowUpReminder time ->', { deviceId, enabled: true, time: e.target.value });
                                                        const res = await setFollowUpReminder({ deviceId, enabled: true, time: e.target.value });
                                                        console.log('[SettingsModal] setFollowUpReminder result:', res?.data);
                                                        if (res?.data?.success) {
                                                            alert('Follow Up 리마인더 시간이 저장되었습니다.');
                                                        }
                                                    } catch (e2) { 
                                                        console.warn('[SettingsModal] setFollowUpReminder failed', e2);
                                                        alert('Follow Up 리마인더 시간 저장 중 오류가 발생했습니다.');
                                                    } finally {
                                                        setFollowUpReminderTimeLoading(false);
                                                    }
                                                }
                                            }}
                                            disabled={!isEnabled || !followUpReminderEnabled || followUpReminderTimeLoading}
                                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center disabled:opacity-50"
                                        />
                                        {followUpReminderTimeLoading && (
                                            <span className="inline-block w-4 h-4 rounded-full border-2 border-blue-300 border-t-blue-600 animate-spin"></span>
                                        )}
                                    </div>
                                    <div className="mt-3 space-y-2 text-sm">
                                        <p className="text-xs text-gray-500 text-left">
                                            미처 챙기지 못한 Follow Up 내용이 있는지 랜덤으로 알려 드립니다
                                        </p>
                                        {!isEnabled && (
                                            <p className="text-xs text-orange-600 text-center mt-1">푸시 알림을 먼저 활성화해주세요</p>
                                        )}
                                    </div>
                                </div>

                                {/* 안내 영역 */}
                                <div className="space-y-3">
                                    {!isSupported && (
                                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                            <div className="flex items-start gap-3">
                                                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                                                <div>
                                                    <p className="text-sm font-medium text-red-800">지원하지 않는 브라우저</p>
                                                    <p className="text-xs text-red-700 mt-1">이 브라우저는 푸시 알림을 지원하지 않습니다.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {isSupported && !isEnabled && permission === 'denied' && (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                                            <div className="flex items-start gap-3">
                                                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                                                <div>
                                                    <p className="text-sm font-medium text-yellow-800">알림이 거부되었습니다</p>
                                                    <p className="text-xs text-yellow-700 mt-1">브라우저 설정에서 이 사이트의 알림을 허용해주세요.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-200" />

                    {/* 동기화(구글 드라이브) - 단일 스냅샷 업/다운로드 */}
                    <div className="space-y-3">
                        <div>
                            <p className="font-medium text-gray-800">동기화(구글 드라이브)</p>
                            <p className="text-xs text-gray-600">마지막 업로드가 기준입니다. 병합은 지원하지 않습니다.</p>
                        </div>
                        <div className={`grid grid-cols-2 gap-3`}>
                            <button
                                onClick={async () => {
                                    if (syncLoading) return;
                                    try {
                                        setSyncPhase('upload');
                                        setSyncLoading(true);
                                        await driveUploadSnapshot();
                                        alert('동기화 업로드 완료');
                                    } catch (e) {
                                        console.error(e);
                                        alert(e?.message || '업로드 실패');
                                    } finally {
                                        setSyncLoading(false);
                                        setSyncPhase(null);
                                    }
                                }}
                                disabled={syncLoading}
                                className={`px-3 py-2 rounded-lg text-white transition-colors ${syncLoading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                            >
                                {syncLoading && syncPhase === 'upload' ? '업로드 중…' : '동기화 업로드'}
                            </button>
                            <button
                                onClick={async () => {
                                    if (syncLoading) return;
                                    try {
                                        setSyncPhase('download');
                                        setSyncLoading(true);
                                        await driveDownloadSnapshot();
                                        alert('동기화 다운로드 완료. 앱을 새로고침합니다.');
                                        window.location.reload();
                                    } catch (e) {
                                        console.error(e);
                                        alert(e?.message || '다운로드 실패');
                                    } finally {
                                        setSyncLoading(false);
                                        setSyncPhase(null);
                                    }
                                }}
                                disabled={syncLoading}
                                className={`px-3 py-2 rounded-lg text-white transition-colors ${syncLoading ? 'bg-gray-600 cursor-not-allowed' : 'bg-gray-800 hover:bg-black'}`}
                            >
                                {syncLoading && syncPhase === 'download' ? '다운로드 중…' : '동기화 다운로드'}
                            </button>
                        </div>
                        {syncLoading && (
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                                <span className="inline-block w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-700 animate-spin"></span>
                                <span>{syncPhase === 'upload' ? '동기화 업로드를 진행 중입니다…' : '동기화 다운로드를 진행 중입니다…'}</span>
                            </div>
                        )}
                        <p className="text-xs text-gray-500">모든 계정 상태에서 동기화(업로드/다운로드)가 가능합니다.</p>
                    </div>

                    <div className="border-t border-gray-200" />

                    {/* 기기 관리 - 모든 상태 표시, 활성 계정만 조작 가능 */}
                    {!linkInfo?.isSecondary && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-gray-800">기기 관리</p>
                                    <p className="text-xs text-gray-600">본 기기와 추가기기 1대를 함께 사용할 수 있습니다.</p>
                                    <p className="text-xs text-red-600 mt-1">기기ID는 사이드바 메뉴의 [앱 정보]섹션에 표시되는 Device ID입니다. 추가하려는 기기에서 fcfunnel.com에 접속하면 확인 가능합니다.</p>
                                </div>
                                {(() => {
                                    if (appStatus === 'active') return <span className="text-xs px-2 py-1 rounded bg-green-50 text-green-700">활성 계정</span>;
                                    if (appStatus === 'trial') return <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">체험 계정</span>;
                                    if (appStatus === 'expired') return <span className="text-xs px-2 py-1 rounded bg-red-50 text-red-700">체험 만료</span>;
                                    if (appStatus === 'subscription_expired') return <span className="text-xs px-2 py-1 rounded bg-red-50 text-red-700">구독 만료</span>;
                                    if (appStatus === 'pending') return <span className="text-xs px-2 py-1 rounded bg-yellow-50 text-yellow-700">검토 대기</span>;
                                    if (appStatus === 'rejected') return <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">승인 거절</span>;
                                    return null;
                                })()}
                            </div>

                            <div className={`p-4 bg-gray-50 rounded-xl space-y-3 ${appStatus === 'active' ? '' : 'opacity-60 pointer-events-none'}`}>
                                <div className="text-sm text-gray-700">
                                    <div>본 기기 ID: <span className="font-mono">{localStorage.getItem('deviceId') || '-'}</span></div>
                                    <div>추가기기 ID: <span className="font-mono">{linkInfo?.linkedSecondaryId || '-'}</span></div>
                                </div>

                                <div className="space-y-2">
                                    <input
                                        type="text"
                                        value={secondaryInput}
                                        onChange={(e) => setSecondaryInput(e.target.value.trim())}
                                        placeholder="추가기기 Device ID 입력"
                                        disabled={appStatus !== 'active'}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                                    />
                                    <div className="flex items-center justify-between gap-2">
                                        <button
                                            disabled={appStatus !== 'active' || linkLoading || !secondaryInput}
                                            onClick={async () => {
                                                if (!secondaryInput) return;
                                                try {
                                                    setLinkLoading(true);
                                                    const primaryId = localStorage.getItem('deviceId');
                                                    const res = await registerSecondaryDevice(primaryId, secondaryInput);
                                                    if (res?.success) {
                                                        alert('추가기기 등록이 완료되었습니다. 추가기기에서 앱을 재시작하세요.');
                                                        const info = await getDeviceLinkInfo(primaryId);
                                                        setLinkInfo(info);
                                                        setSecondaryInput('');
                                                    } else {
                                                        alert(res?.message || '등록 실패');
                                                    }
                                                } catch (e) {
                                                    console.error(e);
                                                    alert(e?.message || '등록 중 오류');
                                                } finally {
                                                    setLinkLoading(false);
                                                }
                                            }}
                                            className={`px-3 py-2 rounded-lg text-white ${linkLoading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                                        >
                                            {linkLoading ? '등록 중…' : '추가기기 등록'}
                                        </button>

                                        <button
                                            disabled={appStatus !== 'active' || linkLoading || !linkInfo?.linkedSecondaryId}
                                            onClick={async () => {
                                                try {
                                                    setLinkLoading(true);
                                                    const primaryId = localStorage.getItem('deviceId');
                                                    const secondaryId = linkInfo?.linkedSecondaryId;
                                                    if (!secondaryId) return;
                                                    const res = await unregisterSecondaryDevice(primaryId, secondaryId);
                                                    if (res?.success) {
                                                        alert('추가기기 연결을 해제했습니다.');
                                                        const info = await getDeviceLinkInfo(primaryId);
                                                        setLinkInfo(info);
                                                    } else {
                                                        alert(res?.message || '해제 실패');
                                                    }
                                                } catch (e) {
                                                    console.error(e);
                                                    alert(e?.message || '해제 중 오류');
                                                } finally {
                                                    setLinkLoading(false);
                                                }
                                            }}
                                            className={`px-3 py-2 rounded-lg ${linkLoading || !linkInfo?.linkedSecondaryId ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-gray-800 text-white hover:bg-black'}`}
                                        >
                                            연결 해제
                                        </button>
                                    </div>
                                </div>

                                <p className="text-xs text-gray-500">최대 2대까지 사용 가능하며, 등록 후 추가기기에서 Google Drive 다운로드로 데이터를 맞춰주세요.</p>
                            </div>
                        </div>
                    )}

                    {linkInfo?.isSecondary && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-gray-800">기기 관리</p>
                                    <p className="text-xs text-gray-600">이 기기는 추가로 등록된 기기입니다.</p>
                                </div>
                                <span className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700">추가기기</span>
                            </div>

                            <div className="p-4 bg-gray-50 rounded-xl space-y-3">
                                <div className="text-sm text-gray-700 space-y-1">
                                    <div>이 기기 ID: <span className="font-mono">{localStorage.getItem('deviceId') || '-'}</span></div>
                                    <div>연결된 본 기기 ID: <span className="font-mono">{linkInfo?.linkedPrimaryId || '-'}</span></div>
                                </div>
                                <p className="text-xs text-gray-500">필요 시 동기화(구글 드라이브)에서 다운로드하여 데이터를 맞춰주세요.</p>
                            </div>
                        </div>
                    )}



                    {/* 업종 선택 (맨 하단 배치) */}
                    <div className="border-t border-gray-200" />
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <p className="font-medium text-gray-800">업종 선택</p>
                            {(() => {
                                try {
                                    const bt = localStorage.getItem('businessType') || 'insurance';
                                    return <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">{bt === 'product' ? '보험 외' : '보험'}</span>;
                                } catch (_) { return null; }
                            })()}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => {
                                    try {
                                        localStorage.setItem('businessType', 'insurance');
                                        window.dispatchEvent(new CustomEvent('businessTypeChanged', { detail: { businessType: 'insurance' } }));
                                    } catch (_) { }
                                }}
                                className={`px-3 py-3 rounded-xl font-bold border text-sm ${(localStorage.getItem('businessType') || 'insurance') === 'insurance' ? 'bg-blue-600 text-white border-blue-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                            >
                                보험
                            </button>
                            <button
                                onClick={() => {
                                    try {
                                        localStorage.setItem('businessType', 'product');
                                        window.dispatchEvent(new CustomEvent('businessTypeChanged', { detail: { businessType: 'product' } }));
                                    } catch (_) { }
                                }}
                                className={`px-3 py-3 rounded-xl font-bold border text-sm ${(localStorage.getItem('businessType') || 'insurance') === 'product' ? 'bg-blue-600 text-white border-blue-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                            >
                                보험 외
                            </button>
                        </div>
                        <p className="text-xs text-gray-500">업종에 따라 앱 내 주요 용어가 자동으로 바뀝니다. 예: 보험 → 제품</p>
                    </div>

                    {/* 하단 닫기 버튼 */}
                    <div className="border-t border-gray-200 pt-6">
                        <button
                            onClick={onClose}
                            className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-bold hover:bg-gray-300 transition-colors"
                        >
                            닫기
                        </button>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default SettingsModal;
